/**
 * Genesys live chat for the Technical support dialog.
 * Genesys Web Messaging deployments must whitelist each application origin
 * (localhost, QA, production) or Messenger.ready will not fire.
 */
import { effect, inject, Injectable, signal } from '@angular/core';
import { Api } from '../api/api.service';
import { GenesysService } from '../genesys/genesys.service';

export type ChatStatus = 'initializing' | 'ready' | 'error';

// Business hours configuration (Central Time)
const BUSINESS_HOURS = {
  startHour: 8, // 8:00 AM
  endHour: 17, // 5:00 PM (17:00)
  timezone: 'America/Chicago', // Central Time
};

@Injectable({
  providedIn: 'root',
})
export class LiveChatService {
  private api = inject(Api);
  private genesys = inject(GenesysService);
  readonly errorTitle = 'Live chat is currently unavailable.';
  readonly errorDetail =
    'Please contact Technical Support during the business hours listed above.';
  private readonly fallbackMessage = `${this.errorTitle} ${this.errorDetail}`;

  private initialized = false;

  // Chat status signal - updated directly for reliable change detection
  chatStatus = signal<ChatStatus>('initializing');

  // Error message signal
  errorMessage = signal<string | null>(null);

  constructor() {
    // Keep local chat state in sync with Genesys service status for fallback
    effect(() => {
      if (this.genesys.status() === 'error') {
        this.chatStatus.set('error');
        this.errorMessage.set(
          this.genesys.errorMessage() || this.fallbackMessage,
        );
        this.removeGenesysLauncherButton();
      }
    });
  }

  /**
   * Checks if current time is within business hours (8 AM - 5 PM Central Time, Monday-Friday)
   */
  isWithinBusinessHours(): boolean {
    try {
      // Get current time in Central timezone
      const now = new Date();
      const centralTime = new Date(
        now.toLocaleString('en-US', { timeZone: BUSINESS_HOURS.timezone }),
      );

      const hour = centralTime.getHours();
      const dayOfWeek = centralTime.getDay(); // 0 = Sunday, 6 = Saturday

      // Check if it's a weekday (Monday = 1 to Friday = 5)
      const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;

      // Check if within business hours (8 AM to 5 PM)
      const isWithinHours =
        hour >= BUSINESS_HOURS.startHour && hour < BUSINESS_HOURS.endHour;

      return isWeekday && isWithinHours;
    } catch {
      // If timezone conversion fails, default to showing chat (fail-open)
      return true;
    }
  }

  /**
   * Initializes Genesys live chat or recreates the button if already initialized
   */
  async doLiveChat(): Promise<void> {
    // Check if within business hours first
    if (!this.isWithinBusinessHours()) {
      this.chatStatus.set('error');
      this.errorMessage.set(this.fallbackMessage);
      this.removeGenesysLauncherButton();
      return;
    }

    // If already initialized and Genesys is ready, just recreate the button
    if (this.initialized && this.genesys.status() === 'ready') {
      this.chatStatus.set('ready');
      this.createGenesysLauncherButton();
      return;
    }

    // If Genesys had a permanent error (not time-based), don't retry
    if (this.initialized && this.genesys.status() === 'error') {
      this.chatStatus.set('error');
      this.errorMessage.set(
        this.genesys.errorMessage() || this.fallbackMessage,
      );
      return;
    }

    if (this.initialized) return;
    this.initialized = true;

    try {
      const config = await this.api.configs.get();
      const genesysSettings = config.liveChat.genesys;

      const success = await this.genesys.initialize(genesysSettings);
      if (success && this.isWithinBusinessHours()) {
        this.chatStatus.set('ready');
        this.createGenesysLauncherButton();
      } else {
        // Genesys failed - set error status and message
        this.chatStatus.set('error');
        this.errorMessage.set(this.genesys.errorMessage());
        this.removeGenesysLauncherButton();
      }
    } catch (error) {
      // Error - set error status
      this.chatStatus.set('error');
      this.errorMessage.set(this.fallbackMessage);
      this.removeGenesysLauncherButton();
    }
  }

  /**
   * Opens the Genesys chat messenger
   */
  openChat(): void {
    this.genesys.open();
  }

  /**
   * Closes the Genesys chat messenger
   */
  closeChat(): void {
    this.genesys.close();
  }

  /**
   * Toggles the Genesys chat messenger open/closed
   */
  toggleChat(): void {
    this.genesys.toggle();
  }

  /**
   * Creates the Genesys custom launcher button in the #tsLiveChat container
   */
  private createGenesysLauncherButton(): void {
    const container = document.getElementById('tsLiveChat');
    if (!container) return;

    // Clear any previous content
    this.clearContainer(container);

    const btn = document.createElement('button');
    btn.id = 'genesysChatButton';
    btn.textContent = 'Live Chat';
    btn.className =
      'p-button p-component tw-bg-[var(--sed-status-blue-color)] tw-text-white tw-px-4 tw-py-2 tw-rounded tw-cursor-pointer tw-font-bold hover:tw-opacity-90';
    btn.style.marginTop = '10px';

    btn.addEventListener('click', () => this.toggleChat());

    container.appendChild(btn);
  }

  /**
   * Removes the Genesys launcher button (fallback when chat unavailable)
   */
  private removeGenesysLauncherButton(): void {
    const container = document.getElementById('tsLiveChat');
    if (!container) return;
    this.clearContainer(container);
  }

  /**
   * Initializes Live Help Now chat
   * Replaced LiveHelpNow by Genesys, thus this method is commented out.
   * private initializeLiveHelpNow(): void {
    if (!this.settings) return;

    // Setup Live Help Now SDK with settings
    (window as any).lhnJsSdkInit = () => {
      lhnJsSdk.setup = {
        application_id: this.settings!.id,
        application_secret: this.settings!.secret,
      };
      lhnJsSdk.controls = [
        {
          type: 'hoc',
          id: this.settings!.controls,
        },
      ];
    };

    (window as any).lhnStatusUpdate = () => {
      const hocButton = document.getElementById('lhnHocButton');
      const tsLiveChat = document.getElementById('tsLiveChat');
      const isOnline =
        typeof lhnJsSdk !== 'undefined' && lhnJsSdk.isOnline === true;

      if (!isOnline) {
        if (hocButton) {
          hocButton.style.display = 'none';
        }
        this.liveHelpNowStatus.set('error');
      } else {
        if (hocButton && tsLiveChat) {
          hocButton.style.position = 'absolute';
          hocButton.style.display = 'block';
          hocButton.style.zIndex = '10';
          hocButton.style.top = '110px';
          hocButton.style.left = '15px';
          hocButton.style.width = '200px';
          hocButton.style.fontSize = '17px';
          tsLiveChat.append(hocButton);
        }
        this.liveHelpNowStatus.set('ready');
      }
    };

    // Dynamically load the Live Help Now SDK script
    const lhnjs = document.getElementsByTagName('script')[0];
    const newjs = document.createElement('script') as HTMLScriptElement;
    newjs.src =
      'https://developer.livehelpnow.net/js/sdk/lhn-jssdk-current.min.js';
    lhnjs.parentNode?.insertBefore(newjs, lhnjs);
  }
  */
  /**
   * Clears all children from a container element
   */
  private clearContainer(container: HTMLElement): void {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  /**
   * Resets the service state for re-initialization
   */
  reset(): void {
    this.initialized = false;
    this.chatStatus.set('initializing');
    this.errorMessage.set(null);
    this.genesys.reset();
  }
}
